import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useAudioEffects } from "../../../hooks/use-audio-effects";
import clickSound from "../../../assets/audio/click.wav";

interface CheckboxProps {
  id: string;
  name: string;
  checked: boolean;
  onChange: ({ name, checked }: { name: string; checked: boolean }) => void;
  label: string;
  disabled?: boolean;
}

const PARTICLE_COUNT = 8;

// Pre-computed so they're stable across renders
const PARTICLES = Array.from({ length: PARTICLE_COUNT }, (_, i) => {
  const angle = (i / PARTICLE_COUNT) * Math.PI * 2;
  const dist = 18 + (i % 2 === 0 ? 6 : 2);
  return {
    x: Math.cos(angle) * dist,
    y: Math.sin(angle) * dist,
    size: i % 3 === 0 ? 4 : 3,
  };
});

export const Checkbox = ({
  id,
  name,
  checked,
  onChange,
  label,
  disabled = false,
}: CheckboxProps) => {
  // Tracks the false → true transition to fire one-shot effects
  const [burstKey, setBurstKey] = useState(0);
  const [burst, setBurst] = useState(false);
  const prevChecked = useRef(checked);
  const { playEffect } = useAudioEffects();

  useEffect(() => {
    if (!checked) {
      setBurst(false);
    }

    if (!prevChecked.current && checked) {
      setBurstKey((k) => k + 1);
      setBurst(true);
      const t = setTimeout(() => setBurst(false), 700);
      return () => clearTimeout(t);
    }
    prevChecked.current = checked;
  }, [checked]);

  return (
    <motion.label
      htmlFor={id}
      onClick={() => {
        if (disabled) return;
        playEffect(clickSound);
        onChange({ name, checked: !checked });
      }}
      className={[
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
        "select-none block",
      ]
        .filter(Boolean)
        .join(" ")}
      // 1. Spring press feel on the whole row
      whileHover={!disabled ? { scale: 1.015 } : {}}
      whileTap={!disabled ? { scale: 0.965 } : {}}
      transition={{ type: "spring", stiffness: 500, damping: 25 }}
    >
      <div className="flex items-center space-x-3">
        {/* ── Checkbox Indicator ── */}
        <div className="relative flex-shrink-0">
          {/* 2. Breathing glow — loops while checked */}
          <motion.div
            className="rounded-md p-[10px]"
            animate={{
              background: checked
                ? "linear-gradient(to bottom, rgba(131,255,86,0.12), rgba(131,255,86,0.55))"
                : "linear-gradient(to bottom, #1f2c39, rgba(32,59,80,0.6))",
              boxShadow: burst
                ? [
                    // low → peak → settle (plays once)
                    "0 0 6px 1px rgba(131,255,86,0.2), inset 0 0 5px rgba(131,255,86,0.05)",
                    "0 0 20px 7px rgba(131,255,86,0.6), inset 0 0 12px rgba(131,255,86,0.2)",
                    "0 0 8px 2px rgba(131,255,86,0.28), inset 0 0 6px rgba(131,255,86,0.08)",
                  ]
                : checked
                  ? "0 0 8px 2px rgba(131,255,86,0.28), inset 0 0 6px rgba(131,255,86,0.08)"
                  : "",
            }}
            transition={{ duration: burst ? 0.7 : 0.4, ease: "easeOut" }}
          >
            {/* 3. Inner square — scale-bounce + color fill */}
            <motion.span
              className="block w-[14px] h-[14px] rounded-sm relative overflow-hidden"
              animate={{
                backgroundColor: checked ? "#83FF56" : "rgba(217,217,217,0.1)",
                scale: checked ? [1, 1.45, 1] : 1,
              }}
              transition={{ duration: 0.3, ease: "backOut" }}
            >
              {/* 4. SVG checkmark draw animation */}
              <AnimatePresence>
                {checked && (
                  <motion.svg
                    key="check"
                    viewBox="0 0 14 14"
                    className="absolute inset-0 w-full h-full p-[1.5px]"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.15 } }}
                  >
                    <motion.path
                      d="M2 7.5L5.5 11L12 3.5"
                      stroke="#0d2e0d"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.28, ease: "easeOut" }}
                    />
                  </motion.svg>
                )}
              </AnimatePresence>
            </motion.span>
          </motion.div>

          {/* 5. Expanding ring — fires once on check */}
          <AnimatePresence>
            {burst && (
              <motion.span
                key={`ring-${burstKey}`}
                className="absolute inset-0 rounded-md border-2 border-[#83FF56] pointer-events-none"
                initial={{ opacity: 0.95, scale: 1 }}
                animate={{ opacity: 0, scale: 2.5 }}
                transition={{ duration: 0.55, ease: "easeOut" }}
              />
            )}
          </AnimatePresence>

          {/* Second, slightly delayed ring for depth */}
          <AnimatePresence>
            {burst && (
              <motion.span
                key={`ring2-${burstKey}`}
                className="absolute inset-0 rounded-md border border-[#83FF56]/50 pointer-events-none"
                initial={{ opacity: 0.7, scale: 1 }}
                animate={{ opacity: 0, scale: 3 }}
                transition={{ duration: 0.7, ease: "easeOut", delay: 0.08 }}
              />
            )}
          </AnimatePresence>

          {/* 6. Particle burst — fires once on check */}
          {burst && (
            <div
              key={`particles-${burstKey}`}
              className="absolute pointer-events-none"
              style={{ top: "50%", left: "50%", width: 0, height: 0 }}
            >
              {PARTICLES.map((p, i) => (
                <motion.span
                  key={i}
                  className="absolute rounded-full bg-[#83FF56]"
                  style={{
                    width: p.size,
                    height: p.size,
                    marginLeft: -p.size / 2,
                    marginTop: -p.size / 2,
                  }}
                  initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                  animate={{
                    x: p.x,
                    y: p.y,
                    opacity: 0,
                    scale: 0,
                  }}
                  transition={{
                    duration: 0.45 + (i % 3) * 0.07,
                    ease: "easeOut",
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Label ── */}
        <motion.div
          className="w-full rounded-md p-2 px-3 relative overflow-hidden"
          animate={{
            background: checked
              ? "linear-gradient(to right, #173531, #17424c)"
              : "linear-gradient(to right, #23323d, rgba(32,59,80,0.7))",
            // 7. Label nudge on activation
            x: burst ? [0, 4, 0] : 0,
          }}
          transition={{ duration: 0.35, ease: "easeOut" }}
        >
          {/* 8. Shimmer sweep — fires once on check */}
          <AnimatePresence>
            {burst && (
              <motion.span
                key={`shimmer-${burstKey}`}
                className="absolute inset-y-0 w-[55%] pointer-events-none"
                style={{
                  background:
                    "linear-gradient(to right, transparent, rgba(131,255,86,0.28), transparent)",
                }}
                initial={{ left: "-55%" }}
                animate={{ left: "145%" }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            )}
          </AnimatePresence>

          {/* 9. Scan line — loops subtly while checked (HUD aesthetic) */}
          <AnimatePresence>
            {checked && (
              <motion.span
                key="scanline"
                className="absolute inset-y-0 w-[35%] pointer-events-none"
                style={{
                  background:
                    "linear-gradient(to right, transparent, rgba(131,255,86,0.07), transparent)",
                }}
                initial={{ left: "-35%" }}
                animate={{ left: "135%" }}
                transition={{
                  duration: 2.4,
                  ease: "linear",
                  repeat: Infinity,
                  repeatDelay: 1.8,
                }}
              />
            )}
          </AnimatePresence>

          {/* 10. Neon text glow */}
          <motion.p
            className="text-[0.835rem] relative z-10 font-medium tracking-wide"
            animate={{
              color: checked ? "#83FF56" : "rgba(255,255,255,0.7)",
              textShadow: checked
                ? "0 0 10px rgba(131,255,86,0.6), 0 0 20px rgba(131,255,86,0.3)"
                : "0 0 0px rgba(131,255,86,0)",
            }}
            transition={{ duration: 0.3 }}
          >
            {label}
          </motion.p>
        </motion.div>
      </div>
    </motion.label>
  );
};
